"use client";

import CloseIcon from "@/assets/icons/ui/guide-popover-close-icon.svg";
import { ReactNode, useRef, useState } from "react";
import Button from "../Button/index";
import { GradientCornerBorder } from "./GradientCornerBorder";

interface SpotlightPopoverProps {
  onNext?: () => void;
  onClose?: () => void;
  className?: string;
  content?: ReactNode;
  currentStep?: number;
  totalSteps?: number;
  videoUrl?: string;
  videoMarkers?: number[];
  buttonText?: string;
  posterUrl?: string;
}

export function SpotlightPopover({
  onNext,
  onClose,
  className,
  content,
  currentStep,
  totalSteps,
  videoUrl,
  videoMarkers,
  posterUrl,
  buttonText,
}: SpotlightPopoverProps) {
  const [currentTime, setCurrentTime] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const defaultButtonText = totalSteps
    ? `Next (${currentStep}/${totalSteps})`
    : "Next";
  const finalButtonText = buttonText || defaultButtonText;

  // Derive active marker index
  const activeMarkerIndex =
    videoMarkers?.reduce((acc, marker, index) => {
      if (currentTime >= marker) return index;
      return acc;
    }, 0) ?? -1;

  const handleMarkerClick = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      if (videoRef.current.paused) {
        videoRef.current.play();
      }
    }
  };

  return (
    <div className={`absolute ${className}`}>
      {/* Popover Container */}
      <div className="w-[306px] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] pointer-events-auto relative">
        {/* Top-left Gradient Border */}
        <GradientCornerBorder width={120} height={120} className="z-20" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 p-[6px] bg-[rgba(0,0,0,0.14)] rounded-full text-text-secondary cursor-pointer backdrop-blur-xl"
        >
          <CloseIcon className="w-[18px] h-[18px]" />
        </button>

        {/* Video Section - 172px height */}
        {videoUrl && (
          <div className="h-[172px] bg-black/20 relative group">
            <video
              ref={videoRef}
              src={videoUrl}
              poster={posterUrl}
              autoPlay
              loop
              muted
              playsInline
              onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
              className="w-full h-full object-cover"
            />

            {/* Carousel Dots */}
            {videoMarkers && videoMarkers.length > 0 && (
              <div className="absolute bottom-[6px] left-1/2 -translate-x-1/2 flex gap-1.5 z-10 transition-opacity">
                {videoMarkers.map((time, index) => (
                  <button
                    key={index}
                    onClick={() => handleMarkerClick(time)}
                    className={`w-[6px] h-[6px] rounded-full transition-all duration-300 ${
                      activeMarkerIndex === index ? "bg-white" : "bg-white/60"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Section with Gradient Background */}
        <div
          className="p-4"
          style={{
            background: "linear-gradient(95deg, #291D39 3.6%, #171518 100%)",
          }}
        >
          <div className="text-sm text-text-primary">{content}</div>
          <div className="flex justify-end mt-4">
            <Button
              type="primary"
              onClick={onNext}
              className="h-7 px-4 text-sm"
            >
              {finalButtonText}
            </Button>
          </div>
        </div>
      </div>

      {/* Triangle Indicator */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-full">
        <svg
          width="32"
          height="12"
          viewBox="0 0 32 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M16 12L0 0H32L16 12Z" fill="#201928" />
        </svg>
      </div>
    </div>
  );
}
